import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserRow } from '../types';
import { useDmrOpenToMe } from './DmrBookingBanner';

/** Speedometer / gauge — a fitting metaphor for NPS (detractor → promoter). */
function SpeedometerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 15a8.5 8.5 0 0 1 17 0" />
      <path d="M4 15h1.4M18.6 15H20M6.3 9.3l1 1M17.7 9.3l-1 1M12 5.5V7" />
      <path d="M12 15l4-3.2" />
      <circle cx="12" cy="15" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Open book — the handbook that explains every tab. */
function ManualIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 6.5S10.2 5 7 5H3.5v12H7c3.2 0 5 1.5 5 1.5" />
      <path d="M12 6.5S13.8 5 17 5h3.5v12H17c-3.2 0-5 1.5-5 1.5" />
      <path d="M12 6.5v12" />
    </svg>
  );
}

/** Speech bubble — the chat rollout tracker. */
function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 12a7 7 0 0 1-7 7H8l-4 3v-4.6A7 7 0 0 1 11 5h2a7 7 0 0 1 7 7Z" />
      <path d="M9.5 11.5h.01M12.5 11.5h.01M15.5 11.5h.01" />
    </svg>
  );
}

/** Calendar — DMR call scheduling. */
function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
      <path d="M8.5 14l2 2 4-4" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15.8 14.6c2.3.2 4 1.8 4.7 4.4" />
    </svg>
  );
}

/**
 * The upper-left "Menu" dropdown. Introduced with the NPS feature as the home
 * for cross-cutting tools that live outside the franchisee tab set. Additive —
 * the existing tabs are untouched. New tools get an entry here.
 */
type MenuItem = {
  label: string;
  description?: string;
  to: string;
  icon?: ReactNode;
  /** When set, only these roles see the item. Omit = everyone. */
  roles?: Array<UserRow['role']>;
  /** Franchisees only see it while DMR booking is open to them. */
  whenDmrOpen?: boolean;
};

const ITEMS: MenuItem[] = [
  {
    label: 'User Manual',
    description: 'How to use every tab, and where each number comes from',
    to: '/user-manual',
    icon: <ManualIcon />,
  },
  {
    label: 'Net Promoter Score',
    description: 'Collect & track NPS from caregivers, clients, and referral sources',
    to: '/nps',
    icon: <SpeedometerIcon />,
  },
  {
    label: 'DMR calls',
    description: 'Book and run Digital Marketing Review calls',
    to: '/dmr',
    icon: <CalendarIcon />,
    // Franchisees see it once the season is open to them (#246).
    roles: ['admin', 'fbc', 'franchisee'],
    whenDmrOpen: true,
  },
  {
    label: 'Franchise partners & FBCs',
    description: 'Claim the franchise partners you look after',
    to: '/fbc-partners',
    icon: <PeopleIcon />,
    roles: ['admin', 'fbc'],
  },
  {
    label: 'Chat rollout',
    description: 'Which franchise sites have switched to the new chat',
    to: '/chat-rollout',
    icon: <ChatIcon />,
    roles: ['admin'],
  },
];

export default function AppMenu({ role }: { role: UserRow['role'] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dmrOpen = useDmrOpenToMe();
  const items = ITEMS.filter(
    (i) => (!i.roles || i.roles.includes(role)) && !(i.whenDmrOpen && role === 'franchisee' && !dmrOpen),
  );

  return (
    <div className="app-menu" ref={wrapRef}>
      <button
        type="button"
        className="app-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="app-menu-icon" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        Menu
      </button>
      {open && (
        <div className="app-menu-popover" role="menu">
          {items.map((item) => (
            <button
              key={item.to}
              type="button"
              role="menuitem"
              className="app-menu-item"
              onClick={() => {
                setOpen(false);
                navigate(item.to);
              }}
            >
              {item.icon && <span className="app-menu-item-icon">{item.icon}</span>}
              <span className="app-menu-item-text">
                <span className="app-menu-item-label">{item.label}</span>
                {item.description && (
                  <span className="app-menu-item-desc">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
